import { useLayoutEffect, useMemo, type ReactNode } from 'react';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { prefixer } from 'stylis';
import rtlPlugin from '@mui/stylis-plugin-rtl';
import { useTranslation } from 'react-i18next';
import { useUiStore } from '@/store';
import { createZaytunTheme } from '@/design-system/theme';
import { ConfirmProvider } from '@/design-system/ConfirmProvider';

export function ThemeProvider({ children }: { children: ReactNode }) {
  const mode = useUiStore(state => state.theme);
  const { i18n } = useTranslation();
  const direction = i18n.dir() as 'rtl' | 'ltr';
  const theme = useMemo(() => createZaytunTheme({ mode, direction }), [mode, direction]);
  const cache = useMemo(() => createCache({ key: direction === 'rtl' ? 'zaytun-rtl' : 'zaytun-ltr', stylisPlugins: direction === 'rtl' ? [prefixer, rtlPlugin] : [prefixer] }), [direction]);
  useLayoutEffect(() => {
    document.documentElement.dataset.theme = mode;
    document.documentElement.dir = direction;
    document.documentElement.lang = i18n.resolvedLanguage ?? i18n.language;
  }, [mode, direction, i18n.resolvedLanguage, i18n.language]);
  return <CacheProvider value={cache}><MuiThemeProvider theme={theme}><ConfirmProvider>{children}</ConfirmProvider></MuiThemeProvider></CacheProvider>;
}
